import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { glorifyApiClient } from '../../../services/api/glorify'
import type { Client, InvoiceSummary } from '../../../services/api/glorify'
import { formatAud, formatDate, invoiceStatusChipClass, invoiceStatusLabel, INVOICE_STATUSES, isoToday } from './invoicingFormat'
import { toSectionError, type SectionError } from './marketsTheme'
import { SectionErrorBanner } from './marketsShared'

// ─── New-invoice inline form ────────────────────────────────────────────────

interface NewInvoiceFormProps {
  clients: Client[]
  onCancel: () => void
  onCreated: (invoiceId: number) => void
}

function NewInvoiceForm({ clients, onCancel, onCreated }: NewInvoiceFormProps): React.JSX.Element {
  const activeClients = useMemo(() => clients.filter((c) => c.is_active), [clients])
  const [clientId, setClientId] = useState<string>(String(activeClients[0]?.client_id ?? ''))
  const [issueDate, setIssueDate] = useState(isoToday())
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<SectionError | null>(null)

  const handleSubmit = async (): Promise<void> => {
    if (!clientId) {
      setError({ kind: 'error', message: 'Select a client before creating the invoice.' })
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      const invoice = await glorifyApiClient.createInvoice({
        client_id: Number(clientId),
        issue_date: issueDate || undefined,
        due_date: dueDate || undefined,
        notes: notes.trim() || undefined,
      })
      onCreated(invoice.invoice_id)
    } catch (err) {
      setError(toSectionError(err, 'Failed to create invoice'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="gd-card gd-card--outlined" style={{ marginBottom: 20 }}>
      <h2 className="gd-section__title" style={{ marginTop: 0 }}>New Invoice</h2>
      <SectionErrorBanner error={error} />
      {activeClients.length === 0 ? (
        <p className="gd-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
          No active clients yet — add one on the Clients page first.
        </p>
      ) : (
        <div className="gd-setup-row">
          <div className="gd-field">
            <label htmlFor="gd-new-invoice-client">Client</label>
            <select
              id="gd-new-invoice-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              {activeClients.map((c) => (
                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
              ))}
            </select>
          </div>
          <div className="gd-field">
            <label htmlFor="gd-new-invoice-issue-date">Issue date</label>
            <input
              id="gd-new-invoice-issue-date"
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="gd-field">
            <label htmlFor="gd-new-invoice-due-date">Due date</label>
            <input
              id="gd-new-invoice-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <div className="gd-field-hint gd-cell-muted">(defaults to settings' payment terms)</div>
          </div>
          <div className="gd-field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="gd-new-invoice-notes">Notes (optional)</label>
            <input
              id="gd-new-invoice-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Shown on the PDF"
            />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          className="gd-btn gd-btn--filled"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting || activeClients.length === 0}
        >
          {isSubmitting ? 'Creating…' : 'Create draft'}
        </button>
        <button className="gd-btn gd-btn--text" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 0 | 1 | 2 | 3 | 4
type ClientFilter = 'all' | number

export default function InvoicesPage(): React.JSX.Element {
  const navigate = useNavigate()

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<SectionError | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [clientFilter, setClientFilter] = useState<ClientFilter>('all')
  const [showNewForm, setShowNewForm] = useState(false)

  const fetchInvoices = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const resp = await glorifyApiClient.listInvoices({
        invoiceStatusId: statusFilter === 'all' ? undefined : statusFilter,
        clientId: clientFilter === 'all' ? undefined : clientFilter,
      })
      setInvoices(resp.invoices)
    } catch (err) {
      setLoadError(toSectionError(err, 'Failed to load invoices'))
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, clientFilter])

  const fetchClients = useCallback(async (): Promise<void> => {
    try {
      const resp = await glorifyApiClient.listClients()
      setClients(resp.clients)
    } catch {
      // Client list failure is non-fatal for the invoices list itself; the
      // "New Invoice" form will show its own empty state.
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchInvoices()
  }, [fetchInvoices])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchClients()
  }, [fetchClients])

  const handleCreated = (invoiceId: number): void => {
    setShowNewForm(false)
    navigate(`/dashboard/invoicing/invoice/${invoiceId}`)
  }

  return (
    <>
      <div className="gd-page-header gd-page-header--with-action">
        <div className="gd-page-header__text">
          <h1>Invoices</h1>
          <p>Create, track and download GST invoices for external clients.</p>
        </div>
        <button className="gd-btn gd-btn--filled has-icon" onClick={() => setShowNewForm((v) => !v)}>
          <span className="material-icons" aria-hidden="true">add</span>
          New Invoice
        </button>
      </div>

      {showNewForm && (
        <NewInvoiceForm
          clients={clients}
          onCancel={() => setShowNewForm(false)}
          onCreated={handleCreated}
        />
      )}

      <SectionErrorBanner error={loadError} />

      {/* Filters */}
      <div className="gd-setup-row">
        <div className="gd-field">
          <label htmlFor="gd-invoices-status-filter">Status</label>
          <select
            id="gd-invoices-status-filter"
            className="gd-select"
            value={statusFilter === 'all' ? 'all' : String(statusFilter)}
            onChange={(e) => setStatusFilter(e.target.value === 'all' ? 'all' : (Number(e.target.value) as StatusFilter))}
          >
            <option value="all">All statuses</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="gd-field">
          <label htmlFor="gd-invoices-client-filter">Client</label>
          <select
            id="gd-invoices-client-filter"
            className="gd-select"
            value={clientFilter === 'all' ? 'all' : String(clientFilter)}
            onChange={(e) => setClientFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading invoices" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">receipt_long</span>
          <p>No invoices match the current filters.</p>
        </div>
      ) : (
        <div className="gd-table-wrap">
          <table className="gd-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Status</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Total</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoice_id}>
                  <td><b>{inv.invoice_number}</b></td>
                  <td>{inv.client_name}</td>
                  <td>
                    <span className={invoiceStatusChipClass(inv.invoice_status_id)}>
                      {invoiceStatusLabel(inv.invoice_status_id)}
                    </span>
                  </td>
                  <td className="gd-cell-muted">{formatDate(inv.issue_date)}</td>
                  <td className="gd-cell-muted">{formatDate(inv.due_date)}</td>
                  <td>{formatAud(inv.total_amount)}</td>
                  <td>
                    <button
                      className="gd-btn gd-btn--outlined"
                      style={{ height: 34, fontSize: 13, padding: '0 16px' }}
                      onClick={() => navigate(`/dashboard/invoicing/invoice/${inv.invoice_id}`)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
